import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlay, FiPause } from 'react-icons/fi';

const AudioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: ${props => props.$isOwn ? '#d9fdd3' : '#fff'};
  border-radius: 8px;
  border: 1px solid ${props => props.$isOwn ? '#b8e6b8' : '#e9edef'};
  min-width: 200px;
  max-width: 300px;
`;

const PlayButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #00a884;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #008f73;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ProgressContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e9edef;
  border-radius: 2px;
  cursor: pointer;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #00a884;
  border-radius: 2px;
  width: ${props => props.$progress}%;
  transition: width 0.1s ease;
`;

const TimeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #667781;
`;

const AudioInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Duration = styled.span`
  font-size: 12px;
  color: #667781;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #00a884;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// HKDF usando Web Crypto API (algoritmo extensível)
async function hkdf(key, salt, info, length) {
  // Usar o algoritmo HKDF extensível via pbkdf2
  const baseKey = await crypto.subtle.importKey(
    'raw',
    key,
    'HKDF',
    false,
    ['deriveBits']
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt,
      info: info
    },
    baseKey,
    length * 8 // bits
  );

  return new Uint8Array(derivedBits);
}

// Função auxiliar para fazer requisição HTTP sem gerar logs no console
function fetchSilently(url) {
  return new Promise((resolve, reject) => {
    // Usar XMLHttpRequest para ter controle total sobre os erros
    const xhr = new XMLHttpRequest();
    
    // Suprimir PROPRIEDADE onerror para evitar que seja chamado
    Object.defineProperty(xhr, 'onerror', {
      get: () => null,
      set: () => {},
      configurable: true
    });
    
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    xhr.timeout = 5000;
    
    xhr.onload = function() {
      if (this.status === 200) {
        resolve({ ok: true, arrayBuffer: () => Promise.resolve(this.response) });
      } else {
        reject(new Error('URL_INVALIDA'));
      }
    };
    
    xhr.ontimeout = function() {
      reject(new Error('URL_INVALIDA'));
    };
    
    // Não usar onerror - evitar completamente
    try {
      xhr.send();
    } catch (e) {
      reject(new Error('URL_INVALIDA'));
    }
  });
}

// Função para descriptografar áudio do WhatsApp no frontend
async function decryptWhatsAppAudio(audioData) {
  try {
    // 1) Baixar o arquivo .enc através do backend para evitar logs de rede no console
    let encryptedData;
    
    try {
      // Usar backend como proxy para evitar logs de erro no navegador
      const response = await fetch(audioData.url, {
        method: 'GET',
        redirect: 'follow'
      }).catch(() => null);
      
      if (!response || !response.ok) {
        throw new Error('URL_INVALIDA');
      }
      
      encryptedData = await response.arrayBuffer();
    } catch (networkError) {
      throw new Error('URL_INVALIDA');
    }

    // 2) Separar corpo e MAC (últimos 10 bytes)
    const encBuf = new Uint8Array(encryptedData);
    const macFromServer = encBuf.slice(encBuf.length - 10);
    const file = encBuf.slice(0, encBuf.length - 10);

    // 3) Derivar chaves via HKDF (112 bytes)
    const mediaType = audioData.mimetype?.startsWith('audio/') ? 'audio' : 'document';
    
    const HKDF_INFO = {
      audio: new TextEncoder().encode('WhatsApp Audio Keys'),
      image: new TextEncoder().encode('WhatsApp Image Keys'), 
      video: new TextEncoder().encode('WhatsApp Video Keys'),
      document: new TextEncoder().encode('WhatsApp Document Keys')
    };
    
    const salt = new Uint8Array(32); // Buffer.alloc(32, 0)
    const info = HKDF_INFO[mediaType];
    const mediaKey = Uint8Array.from(atob(audioData.mediaKey), c => c.charCodeAt(0));
    
    const expanded = await hkdf(mediaKey, salt, info, 112);
    
    const iv = expanded.slice(0, 16);
    const cipherKey = expanded.slice(16, 48);
    const macKey = expanded.slice(48, 80);

    // 4) Validar HMAC (primeiros 10 bytes)
    const dataToMac = new Uint8Array(iv.length + file.length);
    dataToMac.set(iv, 0);
    dataToMac.set(file, iv.length);
    
    const macKeyForHmac = await crypto.subtle.importKey(
      'raw',
      macKey,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const macCalc = await crypto.subtle.sign('HMAC', macKeyForHmac, dataToMac);
    const macCalcTruncated = new Uint8Array(macCalc.slice(0, 10));
    
    // Comparar MACs
    const macsEqual = macCalcTruncated.every((byte, index) => byte === macFromServer[index]);
    
    // Continuar mesmo com HMAC inválido

    // 5) Descriptografar AES-256-CBC
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      cipherKey,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );
    
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv
      },
      cryptoKey,
      file
    );

    // Criar blob com os dados decodificados (OGG/Opus)
    const mimeType = 'audio/ogg; codecs=opus';
    const blob = new Blob([decryptedData], { type: mimeType });
    
    return { success: true, blob, mimeType };

  } catch (error) {
    // Não propagar erro com detalhes técnicos
    if (error.message?.includes('URL_INVALIDA') || error.message?.includes('URL expirada') || error.message?.includes('HTTP')) {
      throw new Error('URL expirada');
    }
    throw new Error('Falha ao descriptografar áudio');
  }
}

const AudioPlayer = ({ audioData, isOwn = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  
  // Suprimir logs de erro do console para requisições de áudio do WhatsApp
  useEffect(() => {
    // Interceptar TODOS os eventos de erro globalmente
    const errorHandler = (event) => {
      if (event.message && typeof event.message === 'string') {
        if (event.message.includes('mmg.whatsapp.net') || 
            event.message.includes('whatsapp') ||
            event.filename?.includes('mmg.whatsapp.net')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    };
    
    window.addEventListener('error', errorHandler, true);
    window.addEventListener('unhandledrejection', errorHandler, true);
    
    // Interceptar console.error
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const message = args.join(' ').toLowerCase();
      if (!message.includes('mmg.whatsapp.net') && 
          !message.includes('whatsapp')) {
        originalConsoleError.apply(console, args);
      }
    };
    
    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', errorHandler, true);
      console.error = originalConsoleError;
    };
  }, []);

  // Formatar tempo em MM:SS
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Carregar áudio quando o componente monta
  useEffect(() => {
    if (audioData && !audioUrl) {
      carregarAudio();
    }
  }, [audioData]);

  // Limpar URL quando o componente desmonta
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const carregarAudio = async () => {
    if (!audioData?.url || !audioData?.mediaKey) {
      setError('Dados de áudio inválidos');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // IMPORTANTE: Detectar se é uma URL antiga/expirada ANTES de fazer requisição HTTP
      // Isso evita que logs de 404 apareçam no DevTools
      const audioAge = audioData.timestamp ? Date.now() - new Date(audioData.timestamp).getTime() : 0;
      const MINUTES_TO_EXPIRE = 60; // URLs do WhatsApp expiram após ~60 minutos
      
      if (audioAge > MINUTES_TO_EXPIRE * 60 * 1000) {
        // URL provavelmente expirada - NÃO fazer requisição HTTP para evitar logs
        throw new Error('URL_EXPIRADA');
      }
      
      // Descriptografar áudio diretamente no frontend usando Web Crypto API
      const result = await decryptWhatsAppAudio(audioData).catch(() => {
        // Interceptar e suprimir erro silenciosamente
        throw new Error('URL_EXPIRADA');
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Erro desconhecido ao descriptografar áudio');
      }
      
      // Criar URL do blob para reprodução
      const url = URL.createObjectURL(result.blob);
      setAudioUrl(url);
      
      // Configurar elemento de áudio
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.load();
      }
    } catch (err) {
      // Tratamento silencioso - apenas exibir mensagem amigável na UI
      let errorMessage = 'Não foi possível carregar o áudio';
      
      if (err.message?.includes('URL_EXPIRADA') || err.message?.includes('expirada') || err.message?.includes('404') || err.message?.includes('inválida')) {
        errorMessage = 'URL do áudio expirou';
      } else if (err.message?.includes('401')) {
        errorMessage = 'Sem autorização';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const clickTime = (clickX / width) * duration;
    
    audioRef.current.currentTime = clickTime;
    setCurrentTime(clickTime);
  };

  if (error) {
    return (
      <AudioContainer $isOwn={isOwn}>
        <div style={{ 
          color: '#ff6b6b', 
          fontSize: '12px',
          textAlign: 'center',
          padding: '8px'
        }}>
          ⚠️ {error}
        </div>
      </AudioContainer>
    );
  }

  return (
    <AudioContainer $isOwn={isOwn}>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      
      <PlayButton 
        onClick={togglePlayPause} 
        disabled={isLoading || !audioUrl}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : isPlaying ? (
          <FiPause size={16} />
        ) : (
          <FiPlay size={16} />
        )}
      </PlayButton>

      <ProgressContainer>
        <ProgressBar onClick={handleProgressClick}>
          <ProgressFill $progress={duration ? (currentTime / duration) * 100 : 0} />
        </ProgressBar>
        <TimeInfo>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </TimeInfo>
      </ProgressContainer>

      <AudioInfo>
        <Duration>
          {audioData?.seconds ? formatTime(audioData.seconds) : '--:--'}
        </Duration>
      </AudioInfo>
    </AudioContainer>
  );
};

export default AudioPlayer;

